export const REGIONAL_PROD_ENVS = ['prodeu1', 'prodcan1', 'prodaus1'] as const;

// Subdomain segment used in alta.avigilon.com URLs for each regional prod env.
// e.g. prodeu1 -> https://access.eu.alta.avigilon.com
export const REGIONAL_PROD_SUBDOMAINS: Record<
  (typeof REGIONAL_PROD_ENVS)[number],
  string
> = {
  prodeu1: 'eu',
  prodcan1: 'can',
  prodaus1: 'aus',
};

export const PROD_ENVS = ['prod', ...REGIONAL_PROD_ENVS] as const;

export const DEVELOPMENT_ENVS = ['mine', 'dev', 'sandbox'] as const;

// Envs we build and deploy to. Deliberately excludes 'test' (see KNOWN_ENVS),
// so this is the right list for build tooling and deploy-target validation.
export const ALL_ENVS = [...DEVELOPMENT_ENVS, ...PROD_ENVS] as const;

export type Env = (typeof ALL_ENVS)[number];

// Every value opConfig.ENV can actually hold at runtime. 'test' is not a
// deploy target, but Jest resolves `openpathConfig` to config.test.json
// (jest.config.js), so ENV === 'test' in every unit test run.
export const KNOWN_ENVS = [...ALL_ENVS, 'test'] as const;

export const isProdEnv = (env: string | undefined): boolean =>
  (PROD_ENVS as readonly string[]).includes(env ?? '');

export const isRegionalProdEnv = (env: string | undefined): boolean =>
  (REGIONAL_PROD_ENVS as readonly string[]).includes(env ?? '');

// Envs a developer builds or deploys to: mine, dev, sandbox.
// NOT the complement of isProdEnv -- 'test' is neither. Use isNonProdEnv when
// you mean "anything that isn't production".
export const isDevelopmentEnv = (env: string | undefined): boolean =>
  (DEVELOPMENT_ENVS as readonly string[]).includes(env ?? '');

// A known env that is not production, i.e. mine, dev, sandbox or test.
// Defined as a true complement of isProdEnv over KNOWN_ENVS so the name cannot
// drift from the behavior: unknown/undefined input is neither prod nor non-prod
// and returns false from both predicates.
export const isNonProdEnv = (env: string | undefined): boolean =>
  (KNOWN_ENVS as readonly string[]).includes(env ?? '') && !isProdEnv(env);

// Longest name first so a URL carrying both a regional and a base segment
// (e.g. `https://helium.prodeu1.prod.openpath.com`) resolves to the more
// specific env. Matching in ALL_ENVS order would return 'prod' there, because
// 'prod' is a prefix of every regional env name and comes first in the list.
const ENV_MATCH_ORDER = [...ALL_ENVS].sort((a, b) => b.length - a.length);

// Detects the region/env from a Helium URL like
// `https://helium.prodeu1.openpath.com` by matching the `.{env}.` segment.
// Note the leading dot: the env must be a middle segment, so a host whose
// first label is the env (`https://prodeu1.prod.openpath.com`) matches 'prod'.
// Falls back to 'mine' if nothing matches.
export const getRegionFromHeliumUrl = (url: string): Env =>
  ENV_MATCH_ORDER.find((env) => url.includes(`.${env}.`)) ?? 'mine';

// Envs with a remotely hosted Helium. 'mine' is excluded: local development
// runs Helium on localhost, at a port the caller configures.
export type RemoteEnv = Exclude<Env, 'mine'>;

// Envs whose Helium still answers on openpath.com. These predate the
// openpath.com -> alta.avigilon.com migration, so they cannot be derived and
// are listed explicitly. Delete an entry as its env moves; when this map is
// empty, getHeliumUrlForEnv is pure derivation and can be inlined.
const LEGACY_OPENPATH_HELIUM_URLS: Partial<Record<RemoteEnv, string>> = {
  dev: 'https://helium.dev.openpath.com',
  sandbox: 'https://helium.sandbox.openpath.com',
  prod: 'https://helium.prod.openpath.com',
  prodeu1: 'https://helium.prodeu1.openpath.com',
};

// Helium API origin for an env, e.g. prodcan1 ->
// `https://helium.prodcan1.alta.avigilon.com`. New regions land on
// alta.avigilon.com and need no entry above.
//
// Note this keys off the full env name, not REGIONAL_PROD_SUBDOMAINS: Helium
// hosts use `prodcan1`, while the user-facing apps use `can`
// (`https://control.can.alta.avigilon.com`).
export const getHeliumUrlForEnv = (env: RemoteEnv): string =>
  LEGACY_OPENPATH_HELIUM_URLS[env] ?? `https://helium.${env}.alta.avigilon.com`;
